import { useEffect, useMemo, useRef, useState } from 'react'
import { reduced } from '../lib/motion'
import { smoothPath } from './smooth'

const ease = (k: number) => 1 - Math.pow(1 - k, 3)
function niceStep(top: number) {
  const raw = top / 4, p = Math.pow(10, Math.floor(Math.log10(raw || 1))), f = raw / p
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p
}
const usd = (v: number) => '$' + Math.round(v).toLocaleString('en-US')
const kLabel = (v: number) => v === 0 ? '$0' : v >= 1000 ? '$' + (v / 1000 >= 10 ? Math.round(v / 1000) : +(v / 1000).toFixed(1)) + 'k' : '$' + v

/**
 * Month to date as one smooth line, last month dotted grey behind it, a soft gradient under the current line.
 * `cur` and `prev` are cumulative values by day of month (index 0 = the 1st). First show: the line draws left to right
 * (1 s) and the end dot fades in; later data (a new month): the line reshapes in 0.6 s. Hover: a crosshair, a dot on each
 * line and a card with both values and the difference. `live` (the current month): the end dot breathes, a soft ring every
 * 2.4 s. Reduce Motion: drawn at once, no ring.
 */
export function MonthLine({ cur, prev, curLabel, prevLabel, days = 31, height = 230, live = false }: { cur: number[]; prev: number[]; curLabel: string; prevLabel: string; days?: number; height?: number; live?: boolean }) {
  const W = 1000, H = height, L = 48, R = 14, T = 10, B = 26
  const svg = useRef<SVGSVGElement>(null)
  const lineRef = useRef<SVGPathElement>(null), areaRef = useRef<SVGPathElement>(null), prevRef = useRef<SVGPathElement>(null)
  const dotRef = useRef<SVGCircleElement>(null), ringRef = useRef<SVGCircleElement>(null), clipRef = useRef<SVGRectElement>(null)
  const drawn = useRef<{ a: [number, number][]; b: [number, number][] } | null>(null)
  const id = useMemo(() => 'ml' + Math.random().toString(36).slice(2, 8), [])
  const [hover, setHover] = useState<{ i: number; left: number } | null>(null)

  const top = Math.max(1, ...cur, ...prev) * 1.05
  const step = niceStep(top), steps = Math.max(1, Math.ceil(top / step)), max = step * steps
  const span = Math.max(days, cur.length, prev.length) - 1 || 1
  const x = (i: number) => L + (i * (W - L - R)) / span
  const y = (v: number) => T + (H - T - B) * (1 - v / max)
  const pa = cur.map((v, i) => [x(i), y(v)] as [number, number])
  const pb = prev.map((v, i) => [x(i), y(v)] as [number, number])

  useEffect(() => {
    const draw = (a: [number, number][], b: [number, number][]) => {
      const d = smoothPath(a)
      lineRef.current?.setAttribute('d', d)
      areaRef.current?.setAttribute('d', a.length ? `${d}L${a[a.length - 1][0]},${H - B}L${a[0][0]},${H - B}Z` : '')
      prevRef.current?.setAttribute('d', smoothPath(b))
      if (a.length) for (const c of [dotRef.current, ringRef.current]) { c?.setAttribute('cx', String(a[a.length - 1][0])); c?.setAttribute('cy', String(a[a.length - 1][1])) }
    }
    let raf = 0
    const run = (ms: number, f: (k: number) => void, delay = 0) => {
      if (reduced()) { f(1); return }
      let t0 = 0
      const tick = (now: number) => { if (!t0) t0 = now + delay; const k = Math.max(0, Math.min(1, (now - t0) / ms)); f(ease(k)); if (k < 1) raf = requestAnimationFrame(tick) }
      raf = requestAnimationFrame(tick)
    }
    const before = drawn.current
    if (!before) {
      draw(pa, pb)
      clipRef.current?.setAttribute('width', '0'); if (dotRef.current) dotRef.current.style.opacity = '0'
      run(1000, (k) => {
        clipRef.current?.setAttribute('width', String((W + 20) * k))
        if (dotRef.current) dotRef.current.style.opacity = String(Math.max(0, (k - 0.85) / 0.15))
      }, 120)
    } else {
      const n = Math.max(pa.length, before.a.length), m = Math.max(pb.length, before.b.length)
      const pad = (p: [number, number][], k: number) => Array.from({ length: k }, (_, i) => p[Math.min(i, p.length - 1)] ?? [x(i), y(0)] as [number, number])
      const A0 = pad(before.a, n), A1 = pad(pa, n), B0 = pad(before.b, m), B1 = pad(pb, m)
      clipRef.current?.setAttribute('width', String(W + 20)); if (dotRef.current) dotRef.current.style.opacity = '1'
      run(600, (k) => {
        const mix = (P: [number, number][], Q: [number, number][]) => Q.map((q, i) => [P[i][0] + (q[0] - P[i][0]) * k, P[i][1] + (q[1] - P[i][1]) * k] as [number, number])
        draw(k < 1 ? mix(A0, A1) : pa, k < 1 ? mix(B0, B1) : pb)
      })
    }
    drawn.current = { a: pa, b: pb }
    return () => cancelAnimationFrame(raf)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cur), JSON.stringify(prev), max])

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const s = svg.current; if (!s) return
    const box = s.getBoundingClientRect(), px = ((e.clientX - box.left) / box.width) * W
    const i = Math.max(0, Math.min(Math.max(cur.length, prev.length) - 1, Math.round(((px - L) / (W - L - R)) * span)))
    setHover({ i, left: (x(i) / W) * 100 })
  }
  const hi = hover?.i ?? -1
  const hc = hi >= 0 && hi < cur.length ? cur[hi] : null, hp = hi >= 0 && hi < prev.length ? prev[hi] : null
  const ticks = [0, 7, 14, 21, 28].filter((d) => d <= span)
  return (
    <div className="ml">
      <svg ref={svg} className="ml__svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${curLabel} month to date ${usd(cur[cur.length - 1] ?? 0)}; ${prevLabel} ${usd(prev[Math.min(cur.length, prev.length) - 1] ?? 0)} at the same point`}>
        <defs>
          <linearGradient id={id + 'g'} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#000" stopOpacity=".09" /><stop offset="100%" stopColor="#000" stopOpacity="0" /></linearGradient>
          <clipPath id={id + 'c'}><rect ref={clipRef} x="0" y="0" width={W + 20} height={H} /></clipPath>
        </defs>
        {Array.from({ length: steps + 1 }, (_, t) => (
          <g key={t}><line className="ml__grid" x1={L} x2={W - R} y1={y(step * t)} y2={y(step * t)} /><text className="ml__tick" x={L - 12} y={y(step * t) + 4} textAnchor="end">{kLabel(step * t)}</text></g>
        ))}
        {ticks.map((d) => <text key={d} className="ml__tick" x={x(d)} y={H - 4} textAnchor="middle">{d + 1}</text>)}
        <path ref={prevRef} className="ml__prev" />
        <path ref={areaRef} fill={`url(#${id}g)`} clipPath={`url(#${id}c)`} />
        <path ref={lineRef} className="ml__cur" clipPath={`url(#${id}c)`} />
        {hi >= 0 && <line className="ml__xh" x1={x(hi)} x2={x(hi)} y1={T} y2={H - B} />}
        {hp != null && <circle className="ml__dotp" r="4.5" cx={x(hi)} cy={y(hp)} />}
        {live && <circle ref={ringRef} className="ml__ring" r="5.5" style={hc != null ? { display: 'none' } : undefined} />}
        <circle ref={dotRef} className="ml__dot" r="5.5" style={hc != null ? { display: 'none' } : undefined} />
        {hc != null && <circle className="ml__dot" r="5.5" cx={x(hi)} cy={y(hc)} />}
        <rect x={L} y={T} width={W - L - R} height={H - T - B} fill="transparent" style={{ cursor: 'crosshair' }} onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
      </svg>
      {hover && (
        <div className="ml__tip" style={{ left: `${hover.left}%`, transform: `translateX(${hover.left > 70 ? 'calc(-100% - 14px)' : '14px'})` }}>
          <b>By day {hi + 1}</b>
          {hc != null && <span><i>{curLabel}</i><b>{usd(hc)}</b></span>}
          {hp != null && <span><i>{prevLabel}</i><em>{usd(hp)}</em></span>}
          {hc != null && hp != null && <span><i>{hc >= hp ? 'Ahead by' : 'Behind by'}</i><b className={hc >= hp ? 'up' : 'dn'}>{usd(Math.abs(hc - hp))}</b></span>}
        </div>
      )}
      <div className="ml__legend"><span><i className="ml__k" />{curLabel}</span><span><i className="ml__k ml__k--prev" />{prevLabel}</span></div>
    </div>
  )
}
